import { useLocation } from "react-router";

import { t } from "i18next";

import { Box } from "@mui/material";

import WeBuyCarsByMakerPage from "@/src/pages/consumer/WeBuyCarsByMaker";

import { PublicRoute } from "@/src/auth/components/PublicRoute";
import type { DealerLogo, DealerLogoData } from "@/src/utils/dealerLogoUtils";
import { findMakerByPath } from "@/src/utils/weBuyCarsLandingPages";

import { loadDealerLogoRouteData } from "../dealerLogoRouteData";
import { buildWeBuyCarsMeta } from "../weBuyCarsMeta";
import type { Route } from "./+types/ostetaan-autoja-maker";

const hasUrl = (logo: DealerLogo): boolean => Boolean(logo.url);

// Maker pages only list dealers that link to their own site.
const loadLinkedDealerLogos = async (): Promise<DealerLogoData> => {
  const { primary, secondary } = await loadDealerLogoRouteData();
  return {
    primary: primary.filter(hasUrl),
    secondary: secondary.filter(hasUrl)
  };
};

// Runs at build time (ssr:false + prerender) so the static HTML carries the
// dealer names and logo markup; images still load lazily in the browser.
export async function loader() {
  return loadLinkedDealerLogos();
}

// Client-side navigations reuse the prerendered data when available. No
// `hydrate = true`, so the initial render uses the inlined build-time data.
export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  try {
    return (await serverLoader()) as Awaited<ReturnType<typeof loader>>;
  } catch {
    return loadLinkedDealerLogos();
  }
}

export const meta: Route.MetaFunction = ({ location }) => {
  const maker = findMakerByPath(location.pathname);
  const title = t(
    maker
      ? "components.weBuyCarsByMakerPage.metaTitleWithMaker"
      : "components.weBuyCarsByMakerPage.metaTitle",
    { carMaker: maker }
  );
  const description = t(
    maker
      ? "components.weBuyCarsByMakerPage.metaDataWithMaker"
      : "components.weBuyCarsByMakerPage.metaData",
    { carMaker: maker }
  );

  return buildWeBuyCarsMeta({
    pathname: location.pathname,
    title,
    description,
    landingName: maker,
    kind: "maker"
  });
};

export default function OstetaanAutojaMakerRoute({
  loaderData
}: Route.ComponentProps) {
  const { pathname } = useLocation();
  const maker = findMakerByPath(pathname);

  return (
    <PublicRoute routeType="consumer" renderWhileAuthenticating>
      <Box sx={{ display: "flex", flex: "1" }}>
        <WeBuyCarsByMakerPage carMaker={maker} dealerLogos={loaderData} />
      </Box>
    </PublicRoute>
  );
}
